<template>
  <div class="schedule-detail-container">
    <!-- 页面头部 -->
    <div class="page-header flex mb-4">
      <div class="left-section">
        <el-button icon="el-icon-arrow-left" type="text" size="medium" class="back-button" @click="goBack">返回</el-button>
      </div>
    </div>

    <!-- 日程基本信息卡片 - 置于选项卡上方 -->
    <el-card shadow="hover" class="basic-info-card mb-4" v-loading="loading">
      <div class="schedule-header">
        <!-- Banner图 -->
        <div class="banner-image" v-if="scheduleData.bannerImage">
          <img :src="scheduleData.bannerImage" class="banner-img" alt="Banner图" />
        </div>
        <div class="banner-image" v-else>
          <div class="empty-banner">
            <i class="el-icon-picture-outline"></i>
            <span>暂无横幅图片</span>
          </div>
        </div>
        
        <!-- 基本信息 -->
        <div class="basic-info-content">
          <h1 class="schedule-title">{{ scheduleData.title || '日程标题' }}</h1>
          <p class="schedule-theme">{{ scheduleData.theme || '日程主题' }}</p>
          
          <div class="info-badges">
            <div class="info-badge">
              <i class="el-icon-date"></i>
              <span>{{ scheduleData.dateId?.formattedDate || '日期未设置' }}</span>
            </div>
            
            <div class="info-badge">
              <i class="el-icon-time"></i>
              <span>{{ formatTimeRange(scheduleData.startTime, scheduleData.endTime) || '时间未设置' }}</span>
            </div>
            
            <div class="info-badge">
              <i class="el-icon-location"></i>
              <span>{{ scheduleData.venueId?.name || '会场未设置' }}</span>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 选项卡 -->
    <el-tabs v-model="activeTab" @tab-click="handleTabChange" class="custom-tabs">
      <el-tab-pane label="日程信息" name="info">
        <el-card shadow="none" class="detail-card" v-loading="loading">
          <!-- 嘉宾信息 -->
          <div class="detail-section" v-if="scheduleData.guests && scheduleData.guests.length > 0">
            <div class="section-title">
              <i class="el-icon-user section-icon"></i>
              <span>嘉宾信息</span>
            </div>
            
            <div class="guests-list">
              <div v-for="guest in scheduleData.guests" :key="guest.guestId" class="guest-card">
                <div class="guest-content">
                  <el-avatar :size="60" :src="guest.guestInfo?.avatar || ''" icon="el-icon-user"></el-avatar>
                  <div class="guest-info">
                    <div class="guest-name">{{ guest.guestInfo?.name || '未知嘉宾' }}</div>
                    <div class="guest-position" v-if="guest.guestInfo?.position">{{ guest.guestInfo.position }}</div>
                    <div class="guest-company" v-if="guest.guestInfo?.company">{{ guest.guestInfo.company }}</div>
                    <div class="guest-role">{{ guest.role || '主持人' }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 跳转按钮配置 -->
          <div class="detail-section" v-if="scheduleData.buttonText || scheduleData.buttonLink">
            <div class="section-title">
              <i class="el-icon-link section-icon"></i>
              <span>跳转按钮配置</span>
            </div>
            
            <div class="action-button">
              <div class="button-preview">
                <el-button type="primary" size="small">
                  <i class="el-icon-link"></i>
                  {{ scheduleData.buttonText || '查看直播' }}
                </el-button>
                <div class="link-box">
                  <div class="link-label">跳转链接</div>
                  <div class="link-url">{{ scheduleData.buttonLink || 'https://live.qingtv.com/event/10002794' }}</div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 日程简介 -->
          <div class="detail-section" v-if="scheduleData.richTextIntro">
            <div class="section-title">
              <i class="el-icon-document section-icon"></i>
              <span>日程简介</span>
            </div>
            <div class="rich-text-content" v-html="scheduleData.richTextIntro"></div>
          </div>
          
          <!-- 其他信息 -->
          <div class="detail-section" v-if="scheduleData.sortOrder">
            <div class="section-title">
              <i class="el-icon-info section-icon"></i>
              <span>其他信息</span>
            </div>
            
            <div class="info-grid other-info">
              <div class="info-item">
                <div class="info-label">排序</div>
                <div class="info-value">
                  <i class="el-icon-sort"></i>
                  {{ scheduleData.sortOrder || '0' }}
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </el-tab-pane>
      
      <el-tab-pane label="议程管理" name="agenda">
        <!-- 议程管理组件 -->
        <AgendaManager 
          :key="scheduleId"
          :scheduleId="scheduleId" 
          :scheduleTimeRange="scheduleTimeRange"
          :venueName="scheduleData.venueId?.name || ''"
        />
      </el-tab-pane>
    </el-tabs>
    
    <!-- 日程编辑对话框 -->
    <el-dialog
      title="编辑日程"
      :visible.sync="editDialogVisible"
      width="800px"
      @close="editDialogVisible = false"
      custom-class="custom-dialog"
    >
      <!-- 复用编辑表单组件或嵌入编辑逻辑 -->
      <div>编辑表单内容待实现</div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveSchedule">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getScheduleDetail } from '@/api/schedule/schedule';
import { getVenueList } from '@/api/schedule/venue';
import { getGuestList, getGuestDetail } from '@/api/guest';
import AgendaManager from '@/pages/mall/schedule/components/AgendaManager.vue';
import { formatDate } from '@/utils/date';

export default {
  name: 'ScheduleDetail',
  
  components: {
    AgendaManager
  },
  
  data() {
    return {
      // 选项卡当前值
      activeTab: 'info',
      
      // 日程ID
      scheduleId: '',
      
      // 日程数据
      scheduleData: {},
      
      // 编辑对话框可见性
      editDialogVisible: false,
      
      // 加载状态
      loading: false,
      
      // 会场选项
      venueOptions: [],
      
      // 嘉宾列表缓存
      guestListCache: []
    };
  },
  
  computed: {
    // 计算日程时间范围，用于传递给议程管理组件
    scheduleTimeRange() {
      if (this.scheduleData.startTime && this.scheduleData.endTime) {
        return {
          startTime: this.scheduleData.startTime,
          endTime: this.scheduleData.endTime
        };
      }
      return null;
    }
  },
  
  created() {
    // 从路由参数获取日程ID
    this.scheduleId = this.$route.query.id;
    
    // 获取URL中的tab参数
    const tab = this.$route.query.tab;
    if (tab && ['info', 'agenda'].includes(tab)) {
      this.activeTab = tab;
    }
    
    // 获取会场列表
    this.fetchVenueList();
    
    if (this.scheduleId) {
      this.fetchScheduleDetail();
    } else {
      this.$message.error('未找到日程ID');
      this.goBack();
    }
  },
  
  methods: {
    // 返回上一页
    goBack() {
      this.$router.go(-1);
    },
    
    // 处理选项卡切换
    handleTabChange(tab) {
      // 更新URL，但不重新加载页面
      this.$router.replace({
        query: { ...this.$route.query, tab: tab.name }
      });
    },
    
    // 获取日程详情
    async fetchScheduleDetail() {
      this.loading = true;
      
      try {
        const response = await getScheduleDetail({ id: this.scheduleId });
        
        // 输出响应数据，用于调试
        console.log('日程详情响应数据:', response);
        
        if (response.status === '10000' || response.code === 200) {
          this.scheduleData = response.data || {};
          
          // 输出日程数据中的日期字段
          console.log('日程日期字段:', this.scheduleData.dateId);
          
          // 如果有嘉宾数据，获取完整的嘉宾信息
          if (this.scheduleData.guests && this.scheduleData.guests.length > 0) {
            await this.fetchGuestsInfo();
          }
        } else {
          this.$message.error(response.message || '获取日程详情失败');
        }
      } catch (error) {
        console.error('获取日程详情出错:', error);
        this.$message.error('获取日程详情失败: ' + (error.message || '未知错误'));
      } finally {
        this.loading = false;
      }
    },
    
    // 获取嘉宾详细信息
    async fetchGuestsInfo() {
      try {
        // 先尝试获取所有嘉宾列表
        await this.fetchAllGuests();
        
        // 处理嘉宾数据，关联详细信息
        const guestPromises = this.scheduleData.guests.map(async guest => {
          const guestId = typeof guest.guestId === 'object' ? guest.guestId._id || guest.guestId.id : guest.guestId;
          
          // 从缓存中查找嘉宾详情
          let guestInfo = this.guestListCache.find(item => 
            item._id === guestId || item.id === guestId
          );
          
          // 如果缓存中找不到，则单独获取嘉宾详情
          if (!guestInfo || !guestInfo.name) {
            console.log(`从缓存中找不到嘉宾 ${guestId}，尝试单独获取`);
            guestInfo = await this.fetchGuestDetail(guestId);
          }
          
          return {
            ...guest,
            guestInfo: guestInfo || { name: '未知嘉宾' }
          };
        });
        
        // 等待所有嘉宾信息获取完成
        this.scheduleData.guests = await Promise.all(guestPromises);
        
        console.log('处理后的嘉宾数据:', this.scheduleData.guests);
      } catch (error) {
        console.error('获取嘉宾信息失败:', error);
      }
    },
    
    // 获取所有嘉宾列表
    async fetchAllGuests() {
      try {
        const response = await getGuestList({ pageSize: 100 });
        
        if (response && response.status === '10000' || response.code === 200) {
          // API可能返回不同结构的数据
          if (Array.isArray(response.data)) {
            this.guestListCache = response.data;
          } else if (response.data && response.data.list) {
            this.guestListCache = response.data.list;
          } else {
            this.guestListCache = [];
          }
          
          console.log('获取到的嘉宾列表:', this.guestListCache);
        }
      } catch (error) {
        console.error('获取嘉宾列表失败:', error);
        this.guestListCache = [];
      }
    },
    
    // 根据ID单独获取嘉宾详情
    async fetchGuestDetail(guestId) {
      try {
        const response = await getGuestDetail(guestId);
        
        if (response && (response.status === '10000' || response.code === 200)) {
          return response.data;
        }
        return null;
      } catch (error) {
        console.error(`获取嘉宾ID ${guestId} 详情失败:`, error);
        return null;
      }
    },
    
    // 处理编辑按钮点击
    handleEdit() {
      this.editDialogVisible = true;
    },
    
    // 保存日程数据
    saveSchedule() {
      // TODO: 实现保存逻辑
      this.editDialogVisible = false;
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return '未设置日期';
      
      // 如果scheduleData.dateId存在且有formattedDate，优先使用它
      if (this.scheduleData.dateId && this.scheduleData.dateId.formattedDate) {
        return this.scheduleData.dateId.formattedDate;
      }
      
      // 尝试格式化日期，如果失败则直接返回原始值
      try {
        // 不同的日期格式可能需要不同的处理方式
        if (typeof date === 'string') {
          // 如果是ISO格式的日期字符串，可以直接创建Date对象
          if (date.includes('T')) {
            const dateObj = new Date(date);
            return dateObj.toLocaleDateString();
          }
          
          // 如果是常规的日期字符串，可能需要替换连字符
          const dateObj = new Date(date.replace(/-/g, '/'));
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleDateString();
          }
        }
        
        // 如果是时间戳或其他格式，尝试直接使用Date构造函数
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          return dateObj.toLocaleDateString();
        }
      } catch (e) {
        console.error('日期格式化错误:', e);
      }
      
      // 如果所有尝试都失败，直接返回原始日期
      return date;
    },
    
    // 格式化时间范围
    formatTimeRange(startTime, endTime) {
      if (!startTime || !endTime) return '';
      return `${startTime} - ${endTime}`;
    },
    
    // 根据会场ID获取会场名称
    getVenueName(venueId) {
      if (!venueId) return '会场未设置';
      
      // 如果venueId是对象
      if (typeof venueId === 'object') {
        return venueId.name || '会场未设置';
      }
      
      // 尝试从会场列表中查找
      const venue = this.venueOptions.find(v => v.value === venueId);
      return venue ? venue.label : '会场未设置';
    },
    
    // 获取状态文本
    statusText(status) {
      const statusMap = {
        '0': '已取消',
        '1': '正常',
        '2': '已结束'
      };
      
      return statusMap[status] || '未知状态';
    },
    
    // 获取状态标签类型
    statusTagType(status) {
      const typeMap = {
        '0': 'danger',
        '1': 'success',
        '2': 'info'
      };
      
      return typeMap[status] || '';
    },
    
    // 获取状态图标
    getStatusIcon(status) {
      const iconMap = {
        '0': 'el-icon-close',
        '1': 'el-icon-check',
        '2': 'el-icon-finished'
      };
      
      return iconMap[status] || 'el-icon-question';
    },
    
    // 获取会场列表
    async fetchVenueList() {
      try {
        const response = await getVenueList();
        if (response && (response.status === '10000' || response.code === 200)) {
          this.venueOptions = (response.data || []).map(venue => ({
            value: venue.id,
            label: venue.name
          }));
          console.log('会场选项:', this.venueOptions);
        }
      } catch (error) {
        console.error('获取会场列表失败:', error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.schedule-detail-container {
  padding: 20px;
  background-color: #f8fafc;
  height: calc(100vh - 60px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  .page-header {
    padding: 0 0 12px;
    border-bottom: 1px solid #e0e7ff;
    margin-bottom: 20px;
    
    .back-button {
      font-size: 15px;
      padding: 4px;
      transition: all 0.2s ease;
      &:hover {
        color: #4f46e5;
        transform: translateX(-2px);
      }
    }
  }
  
  .basic-info-card {
    border-radius: 8px;
    margin-top: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    transition: all 0.3s ease;
    
    &:hover {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    }
    
    .schedule-header {
      display: flex;
      align-items: flex-start;
      
      .banner-image {
        width: 320px; /* 16:9的比例，宽度为320px */
        height: 180px; /* 高度为180px，正好是16:9 */
        margin-right: 24px;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        
        .banner-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
          
          &:hover {
            transform: scale(1.05);
          }
        }
        
        .empty-banner {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background-image: linear-gradient(45deg, #f3f4f6 25%, #e5e7eb 25%, #e5e7eb 50%, #f3f4f6 50%, #f3f4f6 75%, #e5e7eb 75%, #e5e7eb 100%);
          background-size: 20px 20px;
          
          i {
            font-size: 40px;
            color: #94a3b8;
            margin-bottom: 12px;
          }
          
          span {
            font-size: 14px;
            color: #64748b;
          }
        }
      }
      
      .basic-info-content {
        flex: 1;
        padding: 12px 0;
        
        .schedule-title {
          font-size: 22px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 10px 0;
          line-height: 1.2;
        }
        
        .schedule-theme {
          font-size: 14px;
          color: #64748b;
          margin: 0 0 16px 0;
        }
        
        .info-badges {
          display: flex;
          flex-wrap: wrap;
          
          .info-badge {
            display: flex;
            align-items: center;
            margin-right: 20px;
            margin-bottom: 10px;
            background-color: #f1f5f9;
            padding: 5px 10px;
            border-radius: 6px;
            
            i {
              font-size: 16px;
              color: #4f46e5;
              margin-right: 8px;
            }
            
            span {
              font-size: 14px;
              color: #475569;
            }
          }
        }
      }
    }
  }
  
  .custom-tabs {
    margin-top: 24px;
    
    ::v-deep .el-tabs__header {
      margin-bottom: 24px;
      border-bottom: 2px solid #e0e7ff;
    }
    
    ::v-deep .el-tabs__item {
      height: 44px;
      line-height: 44px;
      font-size: 15px;
      color: #64748b;
      padding: 0 20px;
      transition: all 0.2s;
      
      &.is-active {
        font-weight: 600;
        color: #4f46e5;
      }
      
      &:hover {
        color: #4f46e5;
      }
    }
    
    ::v-deep .el-tabs__active-bar {
      height: 3px;
      background-color: #4f46e5;
      border-radius: 3px 3px 0 0;
    }
  }
  
  .detail-card {
    box-shadow: none !important;
    background-color: #fff;
    border-radius: 8px;
    overflow: hidden;
    
    .detail-section {
      margin-bottom: 36px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .section-title {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        position: relative;
        padding-left: 12px;
        
        &:before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 20px;
          background-color: #4f46e5;
          border-radius: 2px;
        }
        
        .section-icon {
          color: #4f46e5;
          margin-right: 8px;
          font-size: 18px;
        }
        
        span {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
        }
      }
    }
    
    .info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 20px;
      
      .info-item {
        display: flex;
        align-items: center;
        background-color: #f8fafc;
        padding: 12px 16px;
        border-radius: 6px;
        transition: all 0.2s;
        
        &:hover {
          background-color: #e0e7ff;
        }
        
        .info-label {
          font-size: 14px;
          color: #64748b;
          margin-right: 8px;
          font-weight: 500;
        }
        
        .info-value {
          font-size: 14px;
          color: #1e293b;
          font-weight: 600;
          display: flex;
          align-items: center;
          
          i {
            margin-right: 6px;
            color: #4f46e5;
          }
        }
      }
    }
    
    .guests-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 20px;
      
      .guest-card {
        background-color: #f8fafc;
        border-radius: 8px;
        padding: 20px;
        transition: all 0.3s ease;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
        
        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
        
        .guest-content {
          display: flex;
          align-items: flex-start;
          
          .el-avatar {
            border: 2px solid #e0e7ff;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            flex-shrink: 0;
          }
          
          .guest-info {
            margin-left: 16px;
            flex: 1;
            
            .guest-name {
              font-size: 16px;
              font-weight: 600;
              color: #1e293b;
              margin-bottom: 8px;
            }
            
            .guest-position {
              font-size: 13px;
              color: #64748b;
              margin-bottom: 4px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            
            .guest-company {
              font-size: 13px;
              color: #64748b;
              margin-bottom: 10px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            
            .guest-role {
              font-size: 12px;
              color: #4f46e5;
              background-color: #e0e7ff;
              padding: 3px 10px;
              border-radius: 12px;
              display: inline-block;
              font-weight: 500;
            }
          }
        }
      }
    }
    
    .rich-text-content {
      background-color: #f8fafc;
      border-radius: 8px;
      padding: 20px;
      line-height: 1.7;
      color: #475569;
      font-size: 15px;
      box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
    }
    
    .action-button {
      .button-preview {
        display: flex;
        align-items: center;
        background-color: #f8fafc;
        padding: 16px;
        border-radius: 8px;
        
        .el-button {
          box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.3);
          transition: all 0.2s;
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 10px -2px rgba(79, 70, 229, 0.4);
          }
        }
        
        .link-box {
          margin-left: 20px;
          
          .link-label {
            font-size: 13px;
            color: #64748b;
            margin-bottom: 6px;
            font-weight: 500;
          }
          
          .link-url {
            font-size: 14px;
            color: #4f46e5;
            word-break: break-all;
            padding: 6px 12px;
            background-color: #e0e7ff;
            border-radius: 4px;
            display: inline-block;
          }
        }
      }
    }
  }
  
  .custom-dialog {
    border-radius: 8px;
    overflow: hidden;
    
    ::v-deep .el-dialog__header {
      padding: 18px 24px;
      background-color: #f8fafc;
      border-bottom: 1px solid #e0e7ff;
    }
    
    ::v-deep .el-dialog__title {
      font-size: 18px;
      font-weight: 600;
      color: #1e293b;
    }
    
    ::v-deep .el-dialog__headerbtn {
      .el-dialog__close {
        font-size: 18px;
        color: #64748b;
        
        &:hover {
          color: #4f46e5;
        }
      }
    }
    
    ::v-deep .el-dialog__body {
      padding: 24px;
    }
    
    ::v-deep .el-dialog__footer {
      padding: 16px 24px;
      border-top: 1px solid #e0e7ff;
    }
  }
}
</style> 